import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Button, Container, Divider, Grid, Paper, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getWorkflows } from '@/redux/slices/profileSlice';
import { selectCanWriteProfiles } from '@/redux/selectors/authSelectors';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { selectAllLanguages, selectJobWorkflows } from '@/redux/selectors/constantsSelectors';
import { findandFormatUTCDateInString } from '@/_utils/date';
import TrackLanguage from '@/components/_common/TrackLanguage';
import ProfileBuilderForm from '../ProfileBuilder/ProfileBuilderForm';
import ProfileBuilderPreviewTemplate from '../ProfileBuilder/ProfileBuilderPreviewTemplate';
import { LabelValue } from '../_common';
import Fieldset from '../_common/Fieldset';
import SchemaDisplay from '../_common/SchemaForm/SchemaDisplay';
import SectionHeading from '../_common/SectionHeading';
import PolarisIcon from '../_common/PolarisIcon';
import AssigneeSelect from '../_common/AssigneeSelect/AssigneeSelect';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import SimpleLabelValue from '../_common/SimpleLabelValue';

const ProfileDetail = ({ profile, editing, setEditing, saveProfile }) => {
  const dispatch = useAppDispatch();
  const jobWorkflows = useAppSelector(selectJobWorkflows);
  const canWriteProfiles = useAppSelector(selectCanWriteProfiles);
  const Languages = useAppSelector(selectAllLanguages);
  const workflows = useAppSelector(selectWorkflows);
  const templateSchema = workflows?.[profile.data.workflow]?.template_schema;

  useEffect(() => {
    dispatch(getWorkflows());
  }, []);
  const workflow = workflows?.[profile.data.workflow];

  const getTracks = (data) => {
    if (!data || !Object.keys(data).length) return '-';
    return Object.keys(data).map((value) => {
      const lang = Languages?.find((l) => l.code === data[value]);
      return (
        <TrackLanguage
          key={value + 1}
          trackNum={(parseInt(value) || 0) + 1}
          trackLang={SimpleLabelValue({ label: lang?.label, value: lang?.code, fallback: data[value] })}
        />
      );
    });
  };

  const audioTracks = getTracks(profile?.data?.audio_tracks);
  const subtitleTracks = getTracks(profile?.data?.subtitle_tracks);

  if (editing) {
    const {
      id,
      name,
      customer_name,
      data: { audio_tracks = {}, subtitle_tracks = {}, ...data }
    } = profile;
    const parsedProfile = {
      id,
      name,
      customer_name,
      ...data,
      audio_tracks: Object.keys(audio_tracks)?.map((key) => audio_tracks[key]),
      subtitle_tracks: Object.keys(subtitle_tracks)?.map((key) => subtitle_tracks[key])
    };
    return <ProfileBuilderForm profile={parsedProfile} setEditing={setEditing} saveProfile={saveProfile} />;
  }

  return (
    <Container>
      <StyledProfilePaper>
        <Grid container>
          <StyledGridProfileInfo item xs={12} md={8} xl={7}>
            <LabelValue label="Created">
              <DateTimeDisplay>{profile.created_at}</DateTimeDisplay>
            </LabelValue>
            <LabelValue label="Created by">
              <AssigneeSelect value={profile.created_by} assigns="user" viewOnly emptyText="-" />
            </LabelValue>
            <LabelValue label="Updated">
              <DateTimeDisplay>{profile.updated_at}</DateTimeDisplay>
            </LabelValue>
            <LabelValue label="Updated by">
              <AssigneeSelect value={profile.updated_by} assigns="user" viewOnly emptyText="-" />
            </LabelValue>
          </StyledGridProfileInfo>

          {!!canWriteProfiles && (
            <Button
              variant="outlined"
              color="primary"
              sx={{ marginLeft: 'auto', alignSelf: 'flex-start' }}
              startIcon={<PolarisIcon icon="edit" height={15} />}
              onClick={setEditing}
            >
              Edit
            </Button>
          )}
        </Grid>

        <StyledGridMetaGroup container alignItems="flex-start" justifyContent="space-between" wrap="nowrap">
          <Grid container alignItems="center" spacing={5}>
            <LabelValue label="Customer">{profile.customer_name}</LabelValue>
            <LabelValue label="Profile">
              <Grid container alignItems="center">
                <StyledTypographyId variant="subtitle1">{profile.id}</StyledTypographyId>
                <span>{findandFormatUTCDateInString(profile.name)}</span>
              </Grid>
            </LabelValue>
            <LabelValue label="Workflow">{jobWorkflows[profile.data?.workflow]}</LabelValue>
          </Grid>
        </StyledGridMetaGroup>

        <Divider sx={{ mb: 4, mt: 6 }} />
        {!workflow || workflow.workflow_version === 'v2' ? null : (
          <>
            <SectionHeading icon={<PolarisIcon icon="components" />}>Components</SectionHeading>
            <Grid container>
              <Grid item xs={12} lg={6}>
                <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                  <Grid item sm={6}>
                    <Fieldset label="Media" color="primary" style={{ height: '100%' }}>
                      <Grid container>
                        <Grid item sm={12}>
                          <LabelValue label="Resolution">{profile?.data?.media_resolution}</LabelValue>
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>
                </Grid>

                <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                  <Grid item sm={12}>
                    <Fieldset label="Audio" color="secondary">
                      <Grid container>
                        <Grid item sm={12}>
                          <LabelValue label="Language Tracks">{audioTracks}</LabelValue>
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>
                </Grid>

                <Grid container columnSpacing={5} sx={{ mb: 3 }}>
                  <Grid item sm={12}>
                    <Fieldset label="Subtitle" color="default">
                      <Grid container>
                        <Grid item sm={9}>
                          <LabelValue label="Language Tracks">{subtitleTracks}</LabelValue>
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        <Grid container>
          <SchemaDisplay schema={templateSchema} inputs={profile.data?.template_inputs} columnSpacing={3} />
        </Grid>

        <Divider sx={{ mb: 4, mt: 6 }} />
        <SectionHeading icon={<PolarisIcon icon="email" />}>Email Notification</SectionHeading>
        <Fieldset label="Delivery Notification" style={{ paddingTop: 40 }}>
          <Grid container spacing={5}>
            <Grid sm={3} item>
              <LabelValue label="Email Templates">{profile?.data?.email_notification?.template}</LabelValue>

              <ProfileBuilderPreviewTemplate
                disabled={!profile?.data?.email_notification?.template}
                template={profile?.data?.email_notification?.template}
              />
            </Grid>

            <Grid sm={3} item>
              <StyledLabelValue label="To:">
                <div style={{ lineHeight: 1.5 }}>{profile?.data?.email_notification?.to?.join('\n') || '-'}</div>
              </StyledLabelValue>
            </Grid>

            <Grid sm={3} item>
              <StyledLabelValue label="CC:">
                <div style={{ lineHeight: 1.5 }}>{profile?.data?.email_notification?.cc?.join('\n') || '-'}</div>
              </StyledLabelValue>
            </Grid>

            <Grid sm={3} item>
              <StyledLabelValue label="BCC:">
                <div style={{ lineHeight: 1.5 }}>{profile?.data?.email_notification?.bcc?.join('\n') || '-'}</div>
              </StyledLabelValue>
            </Grid>
          </Grid>
        </Fieldset>
      </StyledProfilePaper>
    </Container>
  );
};

export default ProfileDetail;

const StyledProfilePaper = styled(Paper)(
  ({ theme }) => `
  padding: ${theme.spacing(5)};

  & > .MuiGrid-container {
    margin-bottom: ${theme.spacing(5)};
  }
`
);

const StyledTypographyId = styled(Typography)(
  ({ theme }) => `
  padding-right: ${theme.spacing(1)};
  line-height: 1;
`
);

const StyledGridMetaGroup = styled(Grid)(
  ({ theme }) => `
  & .meta {
    padding-right: ${theme.spacing(8)};
    display: inline-block;
  }
`
);

const StyledGridProfileInfo = styled(Grid)(
  ({ theme }) => `
  background: ${theme.palette.primary.dark + '1f'};
  padding: ${theme.spacing(2)};
  border-radius: ${theme.shape.borderRadius}px;
  display: flex;
  justify-content: space-between;
`
);

const StyledLabelValue = styled(LabelValue)`
  white-space: pre-line;
`;
